<script setup lang="ts">
  import {onMounted, reactive, ref, watch} from 'vue';
  import {ElMessage} from 'element-plus';
  import {Search} from '@element-plus/icons-vue';
  import {
    fileTypeOptions,
    type DownloadItem,
    type FileTypeFilter,
    type MenuNode,
  } from '../mock';
  import {
    fetchPublicMenuTree,
    fetchPublicDownloadList,
    resolveDownloadUrl,
  } from '../publicApi';
  import DeviceMenuTree from './DeviceMenuTree.vue';
  import zipIcon from '../assets/zip-icon.png';
  import pdfIcon from '../assets/pdf-icon.png';
  import binIcon from '../assets/bin-icon.png';

  const menuTree = ref<MenuNode[]>([]);
  const pageList = ref<DownloadItem[]>([]);
  const total = ref(0);
  const listLoading = ref(false);

  const keyword = ref('');
  const keywordApplied = ref('');
  const deviceSeriesId = ref('all');
  const fileType = ref<FileTypeFilter>('all');
  const page = ref(1);
  const pageSize = 6;

  const expandedMap = reactive<Record<string, boolean>>({});

  function expandAllNodes(nodes: MenuNode[], depth = 0) {
    const list = nodes || [];
    for (let i = 0; i < list.length; i++) {
      const node = list[i];
      // 产品线、系列默认展开
      if (depth < 2) {
        expandedMap[node.id] = true;
        expandAllNodes(node.children || [], depth + 1);
      }
    }
  }

  const loadMenu = async () => {
    menuTree.value = await fetchPublicMenuTree();
    expandAllNodes(menuTree.value);
  };

  const loadList = async () => {
    listLoading.value = true;
    try {
      const result = await fetchPublicDownloadList({
        deviceSeriesId: deviceSeriesId.value,
        fileType: fileType.value,
        keyword: keywordApplied.value,
        page: page.value,
        pageSize: pageSize,
      });
      pageList.value = result.list;
      total.value = result.total;
    } finally {
      listLoading.value = false;
    }
  };

  watch([deviceSeriesId, fileType, keywordApplied], () => {
    if (page.value === 1) {
      loadList();
    } else {
      page.value = 1;
    }
  });

  watch(page, () => {
    loadList();
  });

  onMounted(() => {
    loadMenu();
    loadList();
  });

  const onSearch = () => {
    keywordApplied.value = keyword.value.trim();
  };

  const onSelectDevice = (id: string) => {
    deviceSeriesId.value = id;
  };

  function toggleNode(id: string) {
    expandedMap[id] = !expandedMap[id];
  }

  const onDownload = (item: DownloadItem) => {
    const url = resolveDownloadUrl(item.fileUrl);
    if (!url) {
      ElMessage.warning('暂无下载地址');
      return;
    }
    window.open(url, '_blank');
  };

  const ZIP_EXTS = ['zip', 'rar', '7z'];
  const PDF_EXTS = ['pdf'];
  const BIN_EXTS = ['bin', 'img'];

  const getFileExt = (name: string) => {
    const match = name.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : '';
  };

  const getFileIcon = (item: DownloadItem) => {
    const source = item.fileName || item.title;
    const ext = getFileExt(source);
    if (PDF_EXTS.indexOf(ext) !== -1 || item.fileType === 'doc') {
      return pdfIcon;
    }
    if (BIN_EXTS.indexOf(ext) !== -1 || item.fileType === 'firmware') {
      return binIcon;
    }
    if (ZIP_EXTS.indexOf(ext) !== -1 || item.fileType === 'tool') {
      return zipIcon;
    }
    return zipIcon;
  };
</script>

<template>
  <div class="download-panel">
    <div class="search-bar">
      <el-input
        v-model="keyword"
        class="search-input"
        clearable
        placeholder="请输入关键词进行搜索"
        @keyup.enter="onSearch"
      >
        <template #prefix>
          <el-icon><Search/></el-icon>
        </template>
      </el-input>
      <el-button type="primary" class="search-btn" @click="onSearch">搜索</el-button>
    </div>

    <div class="panel-body">
      <aside class="side-menu">
        <div class="device-type-list">
          <div
            class="device-type-item all-item"
            :class="{ active: deviceSeriesId === 'all' }"
            @click="onSelectDevice('all')"
          >
            全部
          </div>
          <DeviceMenuTree
            :nodes="menuTree"
            :selected-id="deviceSeriesId"
            :expanded-map="expandedMap"
            @select="onSelectDevice"
            @toggle="toggleNode"
          />
        </div>
      </aside>

      <main class="main-area">
        <div class="type-row">
          <span class="type-label">资料类型：</span>
          <button
            v-for="opt in fileTypeOptions"
            :key="opt.value"
            type="button"
            class="type-btn"
            :class="{ active: fileType === opt.value }"
            @click="fileType = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>

        <div class="result-head">
          <span class="result-title">筛选结果</span>
        </div>

        <div v-if="pageList.length" class="card-grid">
          <div
            v-for="item in pageList"
            :key="item.id"
            class="res-card"
            @click="onDownload(item)"
          >
            <div class="file-icon">
              <img class="file-icon-img" :src="getFileIcon(item)" alt=""/>
            </div>
            <div class="card-content">
              <div class="card-top">
                <span class="card-title" :title="item.title">{{ item.title }}</span>
                <span class="card-tag">{{ item.tag }}</span>
              </div>
              <div class="card-desc">
                <single-ellipsis-tooltip
                  :content="item.description || ''"
                  effect="light"
                  placement="top-start"
                >
                  <template #content>
                    <div class="desc-tooltip-content">{{ item.description }}</div>
                  </template>
                </single-ellipsis-tooltip>
              </div>
              <div class="card-time">发布时间：{{ item.publishTime }}</div>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无匹配资源"/>

        <div v-if="total > 0" class="pager-wrap">
          <span class="pager-total">共 {{ total }} 条</span>
          <el-pagination
            v-model:current-page="page"
            :page-size="pageSize"
            :total="total"
            layout="prev, pager, next"
            background
          />
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .download-panel {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px 48px;
  }

  .search-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    position: relative;
    z-index: 2;
  }

  .search-input {
    flex: 1;

    :deep(.eh-input__wrapper) {
      min-height: 48px;
      border-radius: 4px;
      box-shadow: 0 4px 16px rgba(15, 35, 80, 0.08);
    }
  }

  .search-btn {
    height: 48px;
    min-width: 96px;
    font-size: 15px;
  }

  .panel-body {
    display: flex;
    gap: 20px;
    align-items: flex-start;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(15, 35, 80, 0.06);
    padding: 20px;
    min-height: 520px;
  }

  .side-menu {
    width: 240px;
    flex-shrink: 0;
    border-right: 1px solid #eef0f4;
    padding-right: 12px;
    max-height: 640px;
    overflow: auto;
  }

  .device-type-list {
    .all-item {
      display: flex;
      align-items: center;
      height: 44px;
      padding: 0 16px;
      cursor: pointer;
      color: var(--color-text-regular, #606266);
      font-size: 14px;
      border-radius: 5px;
      transition: background-color 0.2s ease;
      user-select: none;

      &:hover {
        background: var(--background-color-primary, #f5f8ff);
      }

      &.active {
        background: var(--background-color-primary, #f5f8ff);
        color: var(--color-primary, #1677ff);
        font-weight: 500;
      }
    }
  }

  .main-area {
    flex: 1;
    min-width: 0;
  }

  .type-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
  }

  .type-label {
    font-size: 14px;
    color: #6b7280;
  }

  .type-btn {
    border: none;
    background: transparent;
    color: #374151;
    font-size: 14px;
    padding: 4px 10px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      color: #1677ff;
    }

    &.active {
      color: #1677ff;
      font-weight: 600;
    }
  }

  .result-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eef0f4;
  }

  .result-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2a37;
  }

  .card-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .res-card {
    display: flex;
    gap: 12px;
    padding: 16px;
    border: 1px solid #e8ebf0;
    border-radius: 8px;
    cursor: pointer;
    transition: box-shadow 0.2s, border-color 0.2s;
    background: #fff;

    &:hover {
      border-color: #b7d1ff;
      box-shadow: 0 4px 14px rgba(22, 119, 255, 0.1);
    }
  }

  .file-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .file-icon-img {
    width: 36px;
    height: 36px;
    object-fit: contain;
    display: block;
  }

  .card-content {
    min-width: 0;
    flex: 1;
  }

  .card-top {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  .card-title {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1677ff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .card-tag {
    flex-shrink: 0;
    font-size: 12px;
    color: #6b7280;
    background: #f3f4f6;
    padding: 2px 8px;
    border-radius: 4px;
    line-height: 1.5;
  }

  .card-desc {
    margin-top: 8px;
    font-size: 13px;
    color: #4b5563;
    line-height: 1.5;
    width: 100%;
    min-width: 0;

    :deep(.ellipsis-single) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .desc-tooltip-content {
    max-width: 320px;
    font-size: 13px;
    line-height: 1.5;
    color: #4b5563;
    white-space: normal;
    word-break: break-all;
  }

  .card-time {
    margin-top: 10px;
    font-size: 12px;
    color: #9ca3af;
  }

  .pager-wrap {
    margin-top: 24px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
  }

  .pager-total {
    font-size: 13px;
    color: #6b7280;
  }

  @media (max-width: 900px) {
    .panel-body {
      flex-direction: column;
    }

    .side-menu {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid #eef0f4;
      padding-right: 0;
      padding-bottom: 12px;
      max-height: 220px;
    }

    .card-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
